import Konva from "konva";
import { KonvaEventObject } from "konva/lib/Node";
import React, { CSSProperties, LegacyRef, Ref } from "react";
import { Image, Layer, Line, Stage } from "react-konva";

import { ImageSettings } from "context/ImageSettingsContext/imageSettingsContext";
import { usePinchAndPanMobile } from "features/canvas/hooks/usePinchAndPanMobile";
import { Dimensions } from "features/canvas/hooks/useResize";
import { BoundingBoxNode, Point, Size } from "features/canvas/types";
import { CanvasAnnotation, CanvasAnnotationUpdater } from "types/imageAnnotations";

import BoundingBox from "../BoundingBox";
import { ShapeType } from "../ShapeRenderer";

function convertToImageSpace(point: Point, canvasDimensions: Dimensions, imageDimensions: Dimensions): Point {
  return {
    x: (point.x / canvasDimensions.width) * imageDimensions.width,
    y: (point.y / canvasDimensions.height) * imageDimensions.height,
  };
}

export type CanvasRendererProps = {
  boxContainerSize: Size;
  canvasRef: Ref<Konva.Stage>;
  style?: CSSProperties;
  currentCursor: CSSProperties["cursor"];
  rotationValue: number;
  offset: Point;
  position: Point;
  onMouseMove: (event: KonvaEventObject<MouseEvent>) => void;
  onMouseUp: (event: KonvaEventObject<MouseEvent>) => void;
  onMouseEnter: (event: KonvaEventObject<MouseEvent>) => void;
  onMouseLeave: (event: KonvaEventObject<MouseEvent>) => void;
  onMouseDown: (event: KonvaEventObject<MouseEvent>) => void;
  onWheel: (event: KonvaEventObject<WheelEvent>) => void;
  onContextMenu: (event: KonvaEventObject<PointerEvent>) => void;
  setIsMouseInsideImage: (isMouseInside: boolean) => void;
  canvasImageRef: LegacyRef<Konva.Image>;
  image?: HTMLCanvasElement;
  renderedImageWidth: number;
  renderedImageHeight: number;
  canDraw: boolean;
  isMouseInsideImage: boolean;
  relativePointerPosition: Point | null;
  rotatedCanvasDimensions: Size;
  showBoundingBoxes: boolean;
  memorizedAnnotations: CanvasAnnotationUpdater[];
  setHoveredNode: (hoveredNode: BoundingBoxNode) => void;
  canInteract: boolean;
  isDragging: boolean;
  selectedAnnotation?: CanvasAnnotation;
  hideLabel: boolean;
  deleteAnnotation: (id: string) => void;
  onAnnotationClick?: (event: KonvaEventObject<MouseEvent>, annotationId: string) => void;
  zoomedDimensions: Size;
  normalImageDimensions: Size;
  hideAnnotationMiddleCross: boolean;
  hideAnnotationDelete: boolean;
  drawingStartPoint: Point | null;
  drawingEndPoint: Point | null;
  label?: string;
  annotationStrokeColor: string;
  annotationStrokeWidth: number;
  annotationFontSize: number;
  annotationShape: ShapeType;
  isExportMode: boolean;
  zoom: number;
  imageSettings: ImageSettings;
  isEditing: boolean;
  rotatedDimensions: Dimensions;
  doubleClickedAnnotation?: CanvasAnnotation | null;
  onDoubleClickAnnotation: (annotation: CanvasAnnotation) => void;
};

export function CanvasRenderer({
  boxContainerSize,
  canvasRef,
  style,
  currentCursor,
  rotationValue,
  offset,
  position,
  onMouseMove,
  onMouseUp,
  onMouseEnter,
  onMouseLeave,
  onMouseDown,
  onWheel,
  onContextMenu,
  setIsMouseInsideImage,
  canvasImageRef,
  image,
  renderedImageWidth,
  renderedImageHeight,
  canDraw,
  isMouseInsideImage,
  relativePointerPosition,
  rotatedCanvasDimensions,
  showBoundingBoxes,
  memorizedAnnotations,
  setHoveredNode,
  canInteract,
  isDragging,
  selectedAnnotation,
  hideLabel,
  deleteAnnotation,
  onAnnotationClick,
  zoomedDimensions,
  normalImageDimensions,
  hideAnnotationMiddleCross,
  hideAnnotationDelete,
  drawingStartPoint,
  drawingEndPoint,
  label,
  annotationStrokeColor,
  annotationStrokeWidth,
  annotationFontSize,
  annotationShape,
  isExportMode,
  zoom,
  imageSettings,
  isEditing,
  rotatedDimensions,
  doubleClickedAnnotation,
  onDoubleClickAnnotation,
}: CanvasRendererProps) {
  const pinchAndPandMobileController = usePinchAndPanMobile();
  const realImageSize: Size = {
    width: image?.width ?? 0,
    height: image?.height ?? 0,
  };

  // zoom difference between rendered canvas image and the exported image size
  // to keep the border width visually proportional
  const imageZoomDifference = realImageSize.width / renderedImageWidth;

  return (
    <Stage
      width={isExportMode ? image?.width : boxContainerSize.width}
      height={isExportMode ? image?.height : boxContainerSize.height}
      ref={canvasRef}
      style={{
        ...(isExportMode ? style : undefined),
        cursor: style?.cursor ?? currentCursor,
        backgroundColor: isExportMode ? undefined : "#C8CDD3",
        display: isExportMode ? "none" : undefined,
      }}
      id="stage-here"
      rotation={rotationValue}
      scaleX={imageSettings.flipX ?? 0 ? -zoom : zoom}
      scaleY={imageSettings.flipY ?? 0 ? -zoom : zoom}
      offset={isExportMode ? undefined : offset}
      x={isExportMode ? undefined : position.x}
      y={isExportMode ? undefined : position.y}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseDown={onMouseDown}
      onWheel={onWheel}
      onTouchMove={pinchAndPandMobileController.handleTouchMove}
      onTouchEnd={pinchAndPandMobileController.handleTouchEnd}
      onContextMenu={onContextMenu}
    >
      <Layer
        imageSmoothingEnabled={false}
        onMouseEnter={() => {
          setIsMouseInsideImage(true);
        }}
        onMouseLeave={() => {
          setIsMouseInsideImage(false);
        }}
      >
        <Image
          ref={canvasImageRef}
          image={image}
          width={isExportMode ? image?.width : renderedImageWidth}
          height={isExportMode ? image?.height : renderedImageHeight}
          x={0}
          y={0}
          filters={[Konva.Filters.Brighten, Konva.Filters.Contrast]}
          brightness={(imageSettings.brightness - 100) / 100}
          contrast={imageSettings.contrast - 100}
          perfectDrawEnabled={false}
        />

        {canDraw && isMouseInsideImage && currentCursor === "crosshair" && isEditing && relativePointerPosition && (
          <>
            <Line
              points={[0, relativePointerPosition.y, rotatedCanvasDimensions.width, relativePointerPosition.y]}
              strokeWidth={1 / zoom}
              stroke="#ffffff80"
              perfectDrawEnabled={false}
            />
            <Line
              points={[relativePointerPosition.x, 0, relativePointerPosition.x, rotatedCanvasDimensions.height]}
              strokeWidth={1 / zoom}
              stroke="#ffffff80"
              perfectDrawEnabled={false}
            />
          </>
        )}

        {showBoundingBoxes &&
          memorizedAnnotations.map((annotation) => {
            const annotationColor = annotation.strokeColor;

            return (
              <BoundingBox
                annotationId={annotation.id}
                canvasPosition={position}
                setHoveredNode={setHoveredNode}
                canInteract={canInteract && !isDragging}
                key={annotation.id}
                isSelected={canInteract && !isExportMode ? selectedAnnotation?.id === annotation.id : false}
                label={!hideLabel ? annotation.label : ""}
                strokeColor={annotationColor}
                strokeWidth={annotation.strokeWidth}
                onDelete={deleteAnnotation}
                onClick={onAnnotationClick}
                imageSettings={imageSettings}
                start={
                  isExportMode
                    ? convertToImageSpace(annotation.start, rotatedDimensions, realImageSize)
                    : annotation.start
                }
                end={
                  isExportMode ? convertToImageSpace(annotation.end, rotatedDimensions, realImageSize) : annotation.end
                }
                isDragging={isDragging}
                canvasDimensions={zoomedDimensions}
                canvasNormalDimensions={normalImageDimensions}
                // In export mode avoid the limit the imageZoomDifference to a
                // minimum of 1 to avoid of the case when rendering a small
                // canvas but the image being making the zoom really small
                // resulting in big annotation borders
                zoom={isExportMode ? zoom / Math.max(1, imageZoomDifference) : zoom}
                imageZoomDifference={isExportMode ? 1 / imageZoomDifference : 1}
                updateBoundingBox={annotation.handleUpdateBBox}
                shape={annotation.shape}
                hideMiddleCross={hideAnnotationMiddleCross}
                hideDelete={hideAnnotationDelete}
                text={annotation.text}
                fontSize={annotation.fontSize}
                onDoubleClick={() => onDoubleClickAnnotation(annotation)}
                isDoubleClicked={canInteract && !isExportMode && doubleClickedAnnotation?.id === annotation.id}
              />
            );
          })}

        {/* This will show the box while the mouse is pressed drawing the new box */}
        {drawingStartPoint && drawingEndPoint && isEditing && (
          <BoundingBox
            canvasPosition={position}
            updateBoundingBox={() => {}}
            label={!hideLabel ? label : ""}
            strokeColor={annotationStrokeColor}
            strokeWidth={annotationStrokeWidth}
            isSelected={annotationShape === "text"}
            start={
              isExportMode
                ? convertToImageSpace(drawingStartPoint, rotatedDimensions, realImageSize)
                : drawingStartPoint
            }
            end={
              isExportMode ? convertToImageSpace(drawingEndPoint, rotatedDimensions, realImageSize) : drawingEndPoint
            }
            setHoveredNode={setHoveredNode}
            canvasDimensions={zoomedDimensions}
            zoom={zoom}
            imageZoomDifference={1}
            canvasNormalDimensions={normalImageDimensions}
            shape={annotationShape}
            hideMiddleCross={hideAnnotationMiddleCross}
            hideDelete={hideAnnotationDelete}
            fontSize={annotationFontSize}
          />
        )}
      </Layer>
    </Stage>
  );
}
